import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { DataProvider } from "@/lib/data";
import { registerServiceWorker } from "@/lib/pushNotifications";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { AppLayout } from "@/components/AppLayout";
import CallsPage from "@/pages/CallsPage";
import LeadsPage from "@/pages/LeadsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AlertSettingsPage from "@/pages/AlertSettingsPage";
import VoicesPage from "@/pages/VoicesPage";
import AdminVoices from "@/pages/AdminVoices";
import { AdminOverview, AdminWorkspaces, AdminUsers } from "@/pages/AdminPages";

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
}

function App() {
  useEffect(() => {
    // Register the service worker once on app load; safe to call repeatedly.
    registerServiceWorker();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <DataProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="system" richColors closeButton />
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Client area */}
              <Route path="/app" element={<ProtectedRoute role="client"><AppLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="calls" replace />} />
                <Route path="calls" element={<CallsPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="voices" element={<VoicesPage />} />
                <Route path="alerts" element={<AlertSettingsPage />} />
              </Route>

              {/* Admin area */}
              <Route path="/admin" element={<ProtectedRoute role="admin"><AppLayout /></ProtectedRoute>}>
                <Route index element={<AdminOverview />} />
                <Route path="workspaces" element={<AdminWorkspaces />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="voices" element={<AdminVoices />} />
              </Route>

              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

          </div>
        </BrowserRouter>
        </DataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
